import React, { useState, useEffect } from "react";
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  Container,
  useScrollTrigger,
  Slide,
  Fade,
  Typography
} from "@mui/material";
import { Link } from "react-scroll";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter"; 
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";

// Hide header on scroll down, show on scroll up
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    threshold: 100 // Only hide after scrolling 100px
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

// Main Header Component
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  // Toggle mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close the drawer when clicking outside
  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  // Change header style based on scroll position
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Determine active section based on scroll position
    const handleScrollSpy = () => {
      const sections = ["home", "features", "modes"];
      const scrollPosition = window.scrollY + 300;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener("scroll", onScroll);
    window.addEventListener("scroll", handleScrollSpy);
    
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", handleScrollSpy);
    };
  }, []);

  // Navigation items
  const navItems = [
    { text: "Home", to: "home" },
    { text: "Features", to: "features" },
    { text: "Modes", to: "modes" }
  ];

  // Social media links
  const socialLinks = [
    { icon: <InstagramIcon sx={{ fontSize: 24 }} />, url: "https://instagram.com/truechampapp", label: "Instagram" },
    { icon: <TwitterIcon sx={{ fontSize: 24 }} />, url: "https://twitter.com/truechampapp", label: "Twitter" }
  ];

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            backdropFilter: isScrolled ? "blur(10px)" : "none",
            transition: "all 0.4s ease",
          }}
        >
          {/* Animated Background */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isScrolled 
                ? "linear-gradient(135deg, rgba(18, 18, 35, 0.85), rgba(40, 40, 80, 0.85))"
                : "transparent",
              borderBottom: isScrolled ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
              transition: "all 0.4s ease",
              zIndex: -1,
            }}
          />

          <Container maxWidth="xl">
            <Toolbar 
              sx={{ 
                justifyContent: "space-between", 
                py: isScrolled ? 0.8 : 1.5,
                transition: "padding 0.4s ease",
              }}
            >
              {/* Left: Logo with hover effect */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Link to="home" smooth duration={800} offset={-100} style={{ cursor: "pointer" }}>
                  <Box display="flex" alignItems="center">
                    <motion.img 
                      src="/logo.png" 
                      alt="TrueChamp Logo" 
                      style={{ 
                        height: isScrolled ? "40px" : "50px",
                        transition: "height 0.4s ease"
                      }}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        ml: 1, 
                        fontWeight: 700, 
                        display: { xs: "none", sm: "block" },
                        fontSize: isScrolled ? "1.1rem" : "1.3rem",
                        transition: "font-size 0.4s ease",
                        color: "white"
                      }}
                    >
                    </Typography>
                  </Box>
                </Link>
              </motion.div>

              {/* Center: Navigation for Desktop */}
              <Fade in={true} timeout={1000}>
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.1)" : "transparent",
                    borderRadius: "999px",
                    padding: isScrolled ? "4px 16px" : "6px 20px",
                    backdropFilter: isScrolled ? "blur(8px)" : "none",
                    border: isScrolled ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                    transition: "all 0.4s ease",
                  }}
                >
                  {navItems.map(({ text, to }, index) => (
                    <Button
                      key={index}
                      sx={{
                        fontSize: "0.9rem",
                        textTransform: "none",
                        fontWeight: 500,
                        color: activeSection === to ? "#9b5af2" : "white",
                        mx: 1,
                        px: 2,
                        py: 0.8,
                        borderRadius: "999px",
                        position: "relative",
                        overflow: "hidden",
                        transition: "color 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          color: "#9b5af2",
                        },
                        "&::after": activeSection === to ? {
                          content: '""',
                          position: "absolute",
                          bottom: 0,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "30%",
                          height: "3px",
                          backgroundColor: "#9b5af2",
                          borderRadius: "3px 3px 0 0",
                        } : {},
                      }}
                    >
                      <Link to={to} smooth duration={800} offset={-100}>
                        {text}
                      </Link>
                    </Button>
                  ))}
                </Box>
              </Fade>

              {/* Right: Social Icons & Mobile Menu */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* Social Media Icons */}
                <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
                  {socialLinks.map((social, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <IconButton 
                        href={social.url} 
                        target="_blank" 
                        aria-label={social.label}
                        sx={{
                          color: "white",
                          background: "rgba(255, 255, 255, 0.1)",
                          backdropFilter: "blur(8px)",
                          "&:hover": {
                            background: "rgba(155, 90, 242, 0.2)",
                            color: "#9b5af2",
                          },
                        }}
                        size="small"
                      >
                        {social.icon}
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>

                {/* Download Button - Desktop Only */}
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <Button
                    variant="contained"
                    component="a"
                    href="https://testflight.apple.com/join/Nkn4cBdH"
                    target="_blank"
                    sx={{
                      backgroundColor: "#9b5af2",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      textTransform: "none",
                      borderRadius: "999px",
                      px: 2.5,
                      py: 0.8,
                      boxShadow: "0 4px 15px rgba(155, 90, 242, 0.3)",
                      "&:hover": {
                        backgroundColor: "#8347d6",
                        boxShadow: "0 6px 20px rgba(155, 90, 242, 0.4)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Download Beta
                  </Button>
                </Box>

                {/* Hamburger Menu - Mobile Only */}
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{ 
                    display: { xs: "flex", md: "none" }, 
                    color: "white",
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(8px)",
                    ml: 1,
                    p: 1.2,
                    "&:hover": {
                      background: "rgba(155, 90, 242, 0.2)",
                    }, 
                  }}
                >
                  <MenuIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: "80%",
            maxWidth: "350px",
            background: "linear-gradient(135deg, rgba(18, 18, 35, 0.95), rgba(40, 40, 80, 0.95))",
            backdropFilter: "blur(10px)",
            color: "white",
            padding: "20px",
            borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
        transitionDuration={400}
      >
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
            >
              {/* Drawer Header: Logo & Close Button */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <img src="/logo.png" alt="TrueChamp Logo" style={{ height: "40px" }} />

                </Box>
                <IconButton 
                  onClick={handleDrawerClose}
                  sx={{ 
                    color: "white",
                    background: "rgba(255, 255, 255, 0.1)",
                    "&:hover": {
                      background: "rgba(155, 90, 242, 0.2)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Navigation Links */}
              <List sx={{ mb: 4 }}>
                {navItems.map(({ text, to }, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.3 }}
                  >
                    <Link
                      to={to}
                      smooth={true}
                      duration={800}
                      offset={-80}
                      onClick={handleDrawerClose}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <ListItem
                        button
                        sx={{
                          mb: 1,
                          py: 1.5,
                          borderRadius: "12px",
                          background: activeSection === to ? "rgba(155, 90, 242, 0.15)" : "transparent",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background: "rgba(155, 90, 242, 0.1)",
                          },
                        }}
                      >
                        <ListItemText 
                          primary={text} 
                          primaryTypographyProps={{ 
                            sx: { 
                              fontSize: "1.1rem", 
                              fontWeight: activeSection === to ? 700 : 500,
                              color: activeSection === to ? "#9b5af2" : "white",
                            } 
                          }} 
                        />
                      </ListItem>
                    </Link>
                  </motion.div>
                ))}
              </List>

              {/* Download Button in Drawer */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  component="a"
                  href="https://testflight.apple.com/join/Nkn4cBdH"
                  target="_blank"
                  sx={{
                    backgroundColor: "#9b5af2",
                    color: "white",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "12px",
                    py: 1.5,
                    fontSize: "1rem",
                    mb: 4,
                    boxShadow: "0 4px 15px rgba(155, 90, 242, 0.3)",
                    "&:hover": {
                      backgroundColor: "#8347d6",
                    },
                  }}
                >
                  Download Beta
                </Button>
              </motion.div>

              {/* Social Icons in Drawer */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                  Connect with us
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  {socialLinks.map((social, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + (0.1 * index), duration: 0.3 }}
                    >
                      <IconButton 
                        href={social.url} 
                        target="_blank" 
                        aria-label={social.label}
                        sx={{
                          color: "white",
                          background: "rgba(255, 255, 255, 0.1)",
                          "&:hover": {
                            background: "rgba(155, 90, 242, 0.2)",
                            color: "#9b5af2",
                          },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Drawer>
    </>
  );
}